// ============================================================================
// CLEO'S SECRET — MembershipPage
// File: src/pages/MembershipPage.jsx
//
// Public marketing page for the commercial offer. Tonight's source of truth
// is the v2 brief delivered in chat (May 2026 rollout). docs/membership-page-
// copy.md is the v1 doc and lags this page until it is rewritten Wednesday.
//
// Eight sections, top to bottom:
//   1. Hook                       — SAND, page-opener with Ankh
//   2. The Diagnostic             — OBS dark, $2,400 one-off product
//   3. After the Diagnostic       — SAND, 3-column program card grid
//                                   (Initiation / Realignment / Mastery,
//                                    Realignment carries MOST RECOMMENDED)
//   4. Understanding the structure— PAP papyrus inset, programs-vs-memberships
//   5. The Three Memberships      — SAND, 3-column TierCard grid
//                                   (Sovereignty carries MOST CHOSEN)
//   6. Additional Care            — PAP papyrus inset between SAND surfaces
//   7. Practical Considerations   — SAND, 9 FAQItems
//   8. Invitation                 — OBS dark, InvitationBlock atom
//
// Commercial non-negotiable: the Diagnostic, the programs, and the
// memberships are three independent payments. No credit, no conversion,
// no bonus, no carryover. The v1 page carried a "Diagnostic fee converts
// to your first six months of membership" line; that line is removed and
// must not be reinstated under any section.
//
// Em-dash conversions from the brief (platform-wide guardrail = no em dashes):
//   • Hook subtitle:       commas in place of dashes
//   • Diagnostic subtitle: "one-off, or as" (comma)
//   • Diagnostic body:     "pathology: sixty-plus" (colon)
//   • Sovereignty inclusion: "team, 24-hour" (comma)
//   • Section 6 (Additional Care): italic-label-then-body, no dash
//   • FAQ Q1: two-sentence break in place of the em dash
//
// All CTAs route to 'intake' for now. The platform doesn't yet have a direct
// membership-purchase or program-enrolment path; the funnel is single-doorway
// through the AI intake.
// ============================================================================

import React from 'react';
import {
  P, T,
  SECTION_PADDING, CONTAINER_READING, CONTAINER_NARROW,
  Eyebrow, SectionDivider, SectionHeadline, BtnPrimary,
} from '../lib/design-tokens';
import Ankh from '../components/Ankh';
import TierCard from '../components/marketing/TierCard';
import FAQItem from '../components/marketing/FAQItem';
import InvitationBlock from '../components/marketing/InvitationBlock';

// ──────────────────────────────────────────────────────────────────────────
// CONTENT — locked verbatim from docs/membership-page-copy.md
// ──────────────────────────────────────────────────────────────────────────

var TIERS = [
  {
    name: 'Knowledge',
    price: '$299',
    cadence: 'per month',
    essence: 'The entry into ongoing clinical care.',
    inclusions: [
      'Quarterly GP review',
      'Full clinical portal access. Your data, your report, your history',
      'Priority booking',
      'Member events and educational programming',
      'Pathology and prescriptions at member rates',
    ],
    ctaLabel: 'Begin Knowledge →',
    highlighted: false,
  },
  {
    name: 'Sovereignty',
    price: '$399',
    cadence: 'per month',
    essence: 'Full clinical decision support, included.',
    inclusions: [
      'Quarterly GP review, with updated care plan',
      'Quarterly pathology <strong>included</strong> in your fee',
      'Clinical messaging with your practitioner team, 24-hour response',
      'Exclusive workshops and member events',
      'Priority booking, always',
    ],
    ctaLabel: 'Begin Sovereignty →',
    highlighted: true,
  },
  {
    name: 'Ritual',
    price: '$499',
    cadence: 'per month',
    essence: 'The deepest cadence of clinical care.',
    inclusions: [
      'Monthly GP review',
      'Quarterly pathology <strong>included</strong>, plus an annual comprehensive assessment',
      'Priority response within 4 hours',
      'VIP events',
      'The highest cadence of clinical access',
    ],
    ctaLabel: 'Begin Ritual →',
    highlighted: false,
  },
];

var PROGRAMS = [
  {
    name: 'Initiation',
    eyebrow: 'PROGRAM ONE',
    essence: 'For a patient starting from clinical fundamentals.',
    body: 'A twelve-week structured journey for a patient whose Diagnostic reveals clear, manageable clinical patterns. Foundations laid in the first four weeks, recalibrated in the middle four, optimised in the final four.',
    inclusions: [
      'Minimum recommended clinical services across 12 weeks',
      'Two mandatory milestones: Week 7 GP review, Week 12 retest',
      'Weekly content, wellbeing check-ins, care team messaging',
    ],
    bundled: 'Includes 12 weeks of Knowledge membership (value approximately $897)',
    ctaLabel: 'Learn about Initiation →',
    highlighted: false,
  },
  {
    name: 'Realignment',
    eyebrow: 'PROGRAM TWO',
    essence: 'For a patient with established clinical complexity.',
    body: 'The standard recommendation. For a patient whose Diagnostic reveals complexity across multiple systems and would benefit from broader allied health, additional GP touch-points, and the depth of a fully-supported recalibration.',
    inclusions: [
      'Additional GP consultations and allied health (dietitian, exercise physiologist)',
      'One supplementary pathology round, plus the two mandatory milestones',
      'Weekly content, wellbeing check-ins, care team messaging',
    ],
    bundled: 'Includes 12 weeks of Sovereignty membership (value approximately $1,197)',
    ctaLabel: 'Learn about Realignment →',
    highlighted: true,
  },
  {
    name: 'Mastery',
    eyebrow: 'PROGRAM THREE',
    essence: 'The deepest cadence of clinical engagement.',
    body: 'For a patient who wants the deepest possible clinical engagement across the twelve weeks: comprehensive allied health, multiple pathology rounds, supplement consultations, structured workshops, and the highest cadence of GP oversight.',
    inclusions: [
      'Maximum included clinical services across 12 weeks',
      'Two mandatory milestones plus multiple pathology rounds',
      'Three-instalment split available (33% / 33% / 34%)',
    ],
    bundled: 'Includes 12 weeks of Ritual membership (value approximately $2,097)',
    ctaLabel: 'Learn about Mastery →',
    highlighted: false,
  },
];

var ADDITIONAL_CARE = [
  {
    label: 'Pathology',
    body: 'When not included in your membership tier, ordered for your story and billed at member rates.',
  },
  {
    label: 'Prescriptions',
    body: 'Fulfilled through partner pharmacies, with member rates where available.',
  },
  {
    label: 'Additional consultations',
    body: 'Beyond your tier cadence, available at member rates.',
  },
  {
    label: 'Specialist referrals',
    body: 'Coordinated by your GP, with continuity of care across providers.',
  },
];

var FAQS = [
  {
    question: 'How does billing work?',
    answer: 'The Diagnostic is paid as a one-off at booking. Programs are paid upfront (Mastery offers a three-instalment split). Memberships are billed monthly, in advance. Your first membership month begins when you choose a tier. This is typically after your Diagnostic if you continue directly into membership, or after your program completes if you enrol in a program first.',
  },
  {
    question: 'Can I choose a different program than my GP recommends?',
    answer: "Yes. The GP's recommendation is clinical guidance, not a binding decision. You can enrol in any of the three programs, regardless of the recommendation. We record both your GP's recommendation and your choice.",
  },
  {
    question: 'Can I skip a program and go straight into membership?',
    answer: 'Yes. Many patients move directly from the Diagnostic into recurring membership without a 12-week program. Your GP will tell you honestly whether a program is the right shape for your work.',
  },
  {
    question: 'Are the Diagnostic, programs, and memberships separate payments?',
    answer: 'Yes. The Diagnostic, programs, and memberships are three independent commercial engagements. The Diagnostic is paid once for the clinical assessment. If you continue into a program, the program fee is paid separately. If you continue into membership, the monthly membership fee is billed separately. There is no credit, conversion, or bonus across the three.',
  },
  {
    question: 'Can I pause my membership?',
    answer: 'Yes. Memberships can be paused for up to three months at a time, with your account held at no cost.',
  },
  {
    question: 'Can I switch tiers?',
    answer: 'Yes. You can move between tiers at any time. Your next billing cycle reflects your new tier.',
  },
  {
    question: 'Is there a minimum commitment?',
    answer: 'No fixed contract. Membership is month-to-month after your Diagnostic.',
  },
  {
    question: 'Telehealth or in-person?',
    answer: 'Your GP consultations can be conducted by telehealth or in-person at our Melbourne practice, depending on what suits you and what your care requires.',
  },
  {
    question: 'What if I move from Melbourne?',
    answer: 'Care continues. Telehealth consultations are available Australia-wide. Pathology can be ordered to any major Australian collection centre.',
  },
];

// ──────────────────────────────────────────────────────────────────────────
// PAGE
// ──────────────────────────────────────────────────────────────────────────

export default function MembershipPage({ onNavigate }) {
  var goIntake = function() { onNavigate('intake'); };

  return (
    <div>

      {/* ═════════ SECTION 1 — HOOK (cream / SAND) ═════════ */}
      <section style={{
        background: P.SAND,
        padding: 'clamp(96px, 9vw, 160px) clamp(24px, 4.5vw, 64px) clamp(72px, 7vw, 120px)',
      }}>
        <div style={{ maxWidth: CONTAINER_NARROW, margin: '0 auto', textAlign: 'center' }}>
          {/* Ankh page-opener */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'clamp(28px, 3vw, 44px)' }}>
            <Ankh responsive="opener" color={P.GO} />
          </div>

          {/* Premise */}
          <p style={{
            ...T.premise,
            color: P.BR2,
            margin: '0 0 24px',
          }}>
            A clinical partnership, by intention.
          </p>

          {/* Title — roman "Choose the cadence that" + italic "fits your care." */}
          <h1 style={{
            ...T.display,
            color: P.INK,
            margin: 0,
          }}>
            <span style={{ display: 'block' }}>Choose the cadence that</span>
            <span style={{
              display: 'block',
              fontStyle: 'italic',
              color: P.BR2,
              fontSize: '0.9em',
            }}>fits your care.</span>
          </h1>

          {/* Subtitle — em dashes from source doc converted to commas, matching
              the comma-replacement pattern used in About page's locked Robyn voice */}
          <p style={{
            ...T.subtitle,
            color: P.BR2,
            margin: 'clamp(28px, 3vw, 40px) auto 0',
            maxWidth: 680,
          }}>
            Three memberships, Knowledge, Sovereignty, and Ritual, each delivering the same clinical model at a different rhythm.
          </p>
        </div>
      </section>

      {/* ═════════ SECTION 2 — THE DIAGNOSTIC (dark / OBS) ═════════ */}
      <section style={{
        background: P.OBS,
        padding: SECTION_PADDING,
        color: P.IVY,
      }}>
        <div style={{ maxWidth: CONTAINER_READING, margin: '0 auto', textAlign: 'center' }}>
          <SectionDivider onDark />
          <div style={{ marginTop: 'clamp(40px, 4vw, 56px)', marginBottom: 28 }}>
            <Eyebrow center color={P.GO2}>BEFORE MEMBERSHIP</Eyebrow>
          </div>
          <SectionHeadline roman="The" italic="Diagnostic." onDark />

          {/* Subtitle — em dash from source doc converted to comma */}
          <div style={{ maxWidth: 640, margin: '0 auto clamp(32px, 3vw, 48px)' }}>
            <p style={{
              ...T.subtitle,
              color: P.GO2,
            }}>
              A complete clinical assessment, available as a one-off, or as the entry into membership.
            </p>
          </div>

          {/* Body — em dash from source doc converted to colon */}
          <div style={{ maxWidth: 720, margin: '0 auto clamp(40px, 4vw, 56px)' }}>
            <p style={{
              ...T.bodyLg,
              color: 'rgba(240, 232, 212, 0.86)',
              textAlign: 'justify',
              hyphens: 'auto',
            }}>
              A ninety-minute clinical consultation with your GP. Comprehensive pathology: sixty-plus biomarkers, ordered for your story. A twelve-page clinical report, written in language you can use. And a clear plan, yours to carry forward.
            </p>
          </div>

          {/* Price callout — large italic, GO2 */}
          <div style={{
            ...T.statNum,
            fontStyle: 'italic',
            color: P.GO2,
            marginBottom: 'clamp(36px, 3.6vw, 52px)',
          }}>
         
          </div>

          {/* CTA. The Diagnostic, the programs, and the memberships are three
              independent commercial engagements — no credit, no conversion,
              no bonus across them. Do not reinstate a "Diagnostic fee
              converts to membership" note here. */}
          <div>
            <BtnPrimary onClick={goIntake} onDark large>Begin with the Diagnostic →</BtnPrimary>
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 3 — AFTER THE DIAGNOSTIC (cream / SAND) ═════════
          Three structured 12-week programs. The middle card (Realignment)
          carries the MOST RECOMMENDED label, the program-grid analogue of
          MOST CHOSEN on the memberships row below. */}
      <section style={{
        background: P.SAND,
        padding: SECTION_PADDING,
      }}>
        <div style={{ maxWidth: CONTAINER_READING, margin: '0 auto', textAlign: 'center' }}>
          <SectionDivider />
          <div style={{ marginTop: 'clamp(40px, 4vw, 56px)', marginBottom: 28 }}>
            <Eyebrow center>WHAT MAY COME NEXT</Eyebrow>
          </div>

          {/* Italic-first headline. SectionHeadline renders roman first, so
              the two italic-first headings on this page (Programs, Structure)
              are inlined to keep the visual rhythm intact. */}
          <h2 style={{
            ...T.display,
            color: P.INK,
            textAlign: 'center',
            margin: '28px auto 32px',
            maxWidth: 1100,
          }}>
            <span style={{
              display: 'block',
              fontStyle: 'italic',
              color: P.BR2,
              fontSize: '0.9em',
            }}>After the Diagnostic,</span>
            <span style={{ display: 'block' }}>a program your GP recommends.</span>
          </h2>
          <div style={{
            width: 'clamp(180px, 22vw, 280px)',
            height: 2,
            background: P.GO,
            opacity: 0.9,
            margin: '0 auto 40px',
          }} />

          {/* Premise — italic gold subtitle */}
          <div style={{ maxWidth: 720, margin: '0 auto clamp(28px, 3vw, 40px)' }}>
            <p style={{
              ...T.subtitle,
              color: P.BR2,
            }}>
              Three twelve-week clinical journeys at three depths. Your GP recommends one based on what the Diagnostic reveals. The choice remains yours.
            </p>
          </div>

          {/* Body paragraph */}
          <div style={{ maxWidth: 720, margin: '0 auto clamp(48px, 5vw, 80px)' }}>
            <p style={{
              ...T.bodyLg,
              color: P.TX,
              textAlign: 'justify',
              hyphens: 'auto',
            }}>
              The Diagnostic is the entry product. It establishes the clinical relationship and reveals what the work ahead actually requires. For most patients, the next step is one of three structured 12-week programs, each anchored to two mandatory clinical milestones, each including 12 weeks of recurring membership at the corresponding tier.
            </p>
          </div>
        </div>

        {/* 3-column program grid — reuses .cleo-tier-grid (1-col <1024px,
            3-col ≥1024px) defined in src/styles/tokens.css. */}
        <div style={{ maxWidth: 1380, margin: '0 auto' }}>
          <div
            className="cleo-tier-grid"
            style={{ marginTop: 0 }}
          >
            {PROGRAMS.map(function(program) {
              return (
                <TierCard
                  key={program.name}
                  name={program.name}
                  price={program.price}
                  cadence={program.cadence}
                  essence={program.essence}
                  body={program.body}
                  inclusions={program.inclusions}
                  bundled={program.bundled}
                  ctaLabel={program.ctaLabel}
                  onCtaClick={goIntake}
                  highlighted={program.highlighted}
                  highlightLabel="Most recommended"
                  hairlineWidth={16}
                />
              );
            })}
          </div>

          {/* Payment-rhythm note below the cards. Three independent payments —
              NO credit-conversion language here, by design. */}
          <p style={{
            ...T.bodyMdItalic,
            color: P.BR2,
            textAlign: 'center',
            maxWidth: 640,
            margin: 'clamp(48px, 4vw, 64px) auto 0',
          }}>
            Programs are paid upfront. Mastery offers a three-instalment payment option.
          </p>
        </div>
      </section>

      {/* ═════════ SECTION 4 — UNDERSTANDING THE STRUCTURE (papyrus / PAP) ═════════
          Quiet explanatory passage between the two card grids. PAP inset
          mirrors the Additional Care section's papyrus-on-sand pattern. */}
      <section style={{
        background: P.PAP,
        padding: SECTION_PADDING,
      }}>
        <div style={{ maxWidth: CONTAINER_READING, margin: '0 auto', textAlign: 'center' }}>
          <SectionDivider />
          <div style={{ marginTop: 'clamp(40px, 4vw, 56px)', marginBottom: 28 }}>
            <Eyebrow center>UNDERSTANDING THE STRUCTURE</Eyebrow>
          </div>

          {/* Italic-first headline. See Programs section for rationale. */}
          <h2 style={{
            ...T.display,
            color: P.INK,
            textAlign: 'center',
            margin: '28px auto 32px',
            maxWidth: 1100,
          }}>
            <span style={{
              display: 'block',
              fontStyle: 'italic',
              color: P.BR2,
              fontSize: '0.9em',
            }}>Two paths,</span>
            <span style={{ display: 'block' }}>one clinical model.</span>
          </h2>
          <div style={{
            width: 'clamp(180px, 22vw, 280px)',
            height: 2,
            background: P.GO,
            opacity: 0.9,
            margin: '0 auto 40px',
          }} />

          {/* Premise */}
          <div style={{ maxWidth: 720, margin: '0 auto clamp(40px, 4vw, 56px)' }}>
            <p style={{
              ...T.subtitle,
              color: P.BR2,
            }}>
              Programs are structured 12-week journeys. Memberships are ongoing relationships. They are connected, but each does distinct work.
            </p>
          </div>

          {/* Three body paragraphs */}
          <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'left' }}>
            <p style={{
              ...T.bodyLg,
              color: P.TX,
              textAlign: 'justify',
              hyphens: 'auto',
              marginBottom: 'clamp(20px, 2vw, 28px)',
            }}>
              Programs are a structured 12-week clinical journey with a defined arc, defined services, and a defined endpoint. They are recommended by your GP after the Diagnostic, based on what the Diagnostic reveals about the work ahead. The bundled membership is included for the twelve weeks of the program, after which the membership continues at the standard rate, or you change tier, pause, or step away.
            </p>
            <p style={{
              ...T.bodyLg,
              color: P.TX,
              textAlign: 'justify',
              hyphens: 'auto',
              marginBottom: 'clamp(20px, 2vw, 28px)',
            }}>
              Memberships are the long-arc clinical relationship, billed monthly, with no fixed end. They can begin after a program ends, after a Diagnostic without a program, or as a standalone path (though most patients should begin with the Diagnostic first).
            </p>
            <p style={{
              ...T.bodyLg,
              color: P.TX,
              textAlign: 'justify',
              hyphens: 'auto',
              marginBottom: 0,
            }}>
              The two paths are connected, not alternatives. A program is twelve weeks of focused work followed by ongoing membership. A direct-to-membership path is the same ongoing care without the structured journey. Your GP will recommend the right starting shape after the Diagnostic.
            </p>
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 5 — THE THREE MEMBERSHIPS (cream / SAND) ═════════ */}
      <section style={{
        background: P.SAND,
        padding: SECTION_PADDING,
      }}>
        <div style={{ maxWidth: 1380, margin: '0 auto' }}>
          <SectionDivider />

          {/* 3-column grid — responsive via .cleo-tier-grid (1-col <1024px, 3-col ≥1024px) */}
          <div
            className="cleo-tier-grid"
            style={{ marginTop: 'clamp(48px, 5vw, 80px)' }}
          >
            {TIERS.map(function(tier) {
              return (
                <TierCard
                  key={tier.name}
                  name={tier.name}
                  price={tier.price}
                  cadence={tier.cadence}
                  essence={tier.essence}
                  inclusions={tier.inclusions}
                  ctaLabel={tier.ctaLabel}
                  onCtaClick={goIntake}
                  highlighted={tier.highlighted}
                />
              );
            })}
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 6 — ADDITIONAL CARE (papyrus / PAP inset) ═════════ */}
      <section style={{
        background: P.PAP,
        padding: SECTION_PADDING,
      }}>
        <div style={{ maxWidth: CONTAINER_READING, margin: '0 auto', textAlign: 'center' }}>
          <SectionDivider />
          <div style={{ marginTop: 'clamp(40px, 4vw, 56px)', marginBottom: 28 }}>
            <Eyebrow center>BEYOND YOUR MEMBERSHIP</Eyebrow>
          </div>
          <SectionHeadline roman="Additional care," italic="at member rates." />

          <div style={{ maxWidth: 640, margin: '0 auto clamp(40px, 4vw, 56px)' }}>
            <p style={{
              ...T.subtitle,
              color: P.BR2,
            }}>
              Some clinical services sit outside the membership fee and are charged at preferential rates.
            </p>
          </div>

          {/* 4-line block — 2-line treatment per item: italic gold label, then body.
              Replaces the em-dash-separator pattern from the source doc. */}
          <div style={{ maxWidth: 580, margin: '0 auto' }}>
            {ADDITIONAL_CARE.map(function(item, i) {
              var isLast = i === ADDITIONAL_CARE.length - 1;
              return (
                <div key={item.label} style={{
                  textAlign: 'center',
                  marginBottom: isLast ? 0 : 'clamp(28px, 2.6vw, 36px)',
                }}>
                  <div style={{
                    ...T.bodyMdItalic,
                    color: P.BR2,
                    marginBottom: 8,
                  }}>{item.label}</div>
                  <p style={{
                    ...T.bodyMd,
                    color: P.TX,
                  }}>{item.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 7 — PRACTICAL CONSIDERATIONS (cream / SAND) ═════════ */}
      <section style={{
        background: P.SAND,
        padding: SECTION_PADDING,
      }}>
        <div style={{ maxWidth: CONTAINER_READING, margin: '0 auto' }}>
          <div style={{ textAlign: 'center' }}>
            <SectionDivider />
            <div style={{ marginTop: 'clamp(40px, 4vw, 56px)', marginBottom: 28 }}>
              <Eyebrow center>COMMONLY ASKED</Eyebrow>
            </div>
            <SectionHeadline roman="Practical" italic="considerations." />

            <div style={{ maxWidth: 640, margin: '0 auto clamp(48px, 4vw, 64px)' }}>
              <p style={{
                ...T.subtitle,
                color: P.BR2,
              }}>
                The everyday questions women ask before they begin.
              </p>
            </div>
          </div>

          {/* FAQs — flat list, all answers visible, no accordion */}
          <div>
            {FAQS.map(function(faq, i) {
              var isLast = i === FAQS.length - 1;
              return (
                <FAQItem
                  key={faq.question}
                  question={faq.question}
                  answer={faq.answer}
                  isLast={isLast}
                />
              );
            })}
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 8 — INVITATION (dark / OBS) ═════════ */}
      <section style={{
        background: P.OBS,
        padding: SECTION_PADDING,
        color: P.IVY,
      }}>
        <InvitationBlock onDark onCtaClick={goIntake} />
      </section>

    </div>
  );
}
